import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import { withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import DoneIcon from '@material-ui/icons/Done';
import ErrorIcon from '@material-ui/icons/ErrorOutlined';
import PropTypes from 'prop-types';
import React from 'react';
import DeleteAdornment from '../core/DeleteAdornment';
import FormError from '../core/FormError';
import { checkValidity, createLengthWarning, sanitiseText } from '../core/utils/utils';
import { globalStyles as styles } from './style/gstheme';
import { DATATYPE_LIMITS } from './utils/constants';
import Datatype from './Datatype';

class LiteralConfirmationField extends React.Component {

    constructor(props) {
        super(props);
        let isValidPattern = true;
        if (props.pattern && props.text !== "") {
            try {
                const re = new RegExp(this.props.pattern);
                isValidPattern = re.test(props.text);
            } catch (e) {
                isValidPattern = false;
            }
        }
        this.state = {
            originalText: props.text,
            text: props.text,
            existingValues: [],
            existing: false,
            needsValidating: false,
            isValidPattern,
            error: !isValidPattern
        };
    }

    static getDerivedStateFromProps(props, state) {
        if (state.text && state.text !== "" && props.existingValues && props.existingValues.length > 0) {
            let hasChanged = true;
            if (props.existingValues.length === state.existingValues.length) {
                hasChanged = props.existingValues.some(ev => {
                    return !state.existingValues.some(sv => {
                        if (sv.text) {
                            return sv.text === ev.text && ev.datatype === sv.datatype;
                        }
                        else {
                            return sv === ev;
                        }
                    });
                });
            }
            else hasChanged = true;
            if (hasChanged) {
                return {
                    needsValidating: true,
                    existingValues: props.existingValues,
                    ...(props.text !== state.originalText) && {
                        text: props.text,
                        originalText: props.text
                    }
                };
            }
        }
        if (props.text !== state.originalText) {
            return {
                text: props.text,
                originalText: props.text
            };
        }
        return null;
    }

    componentDidUpdate() {
        if (this.state.needsValidating && this.state.text && this.state.text !== "") this.checkValidity();
    }

    checkValidity() {
        const { datatype, existingValues } = this.props;
        const text = this.state.text.toString().trim();
        if (text === "" || text === this.state.originalText) {
            this.setState({ error: false, existing: false, needsValidating: false });
            return;
        }
        let validity = checkValidity(text, datatype, existingValues, this.props.maxLength);
        let isValidPattern = true;
        if (validity.isValid && this.props.pattern) {
            try {
                const re = new RegExp(this.props.pattern);
                isValidPattern = re.test(text);
                validity.isValid = isValidPattern;
            } catch (e) {
                validity.isValid = false;
            }
        }
        this.setState({ error: !validity.isValid, isValidPattern, existing: validity.existing, needsValidating: false });
    }

    handleChange = (event) => {
        const t = event.target;
        let trimmedText = t.value.trimLeft();
        if (!this.props.allowSpaces) {
            trimmedText = trimmedText.replace(/\s/g, '');
        }
        const cleanText = sanitiseText(trimmedText);
        this.setState({ text: cleanText }, this.checkValidity);
    };

    reset = () => {
        let isValidPattern = true;
        if (this.props.pattern) {
            try {
                const re = new RegExp(this.props.pattern);
                isValidPattern = re.test(this.state.originalText);
            } catch (e) {
                isValidPattern = false;
            }
        }
        this.setState({
            text: this.state.originalText,
            isValidPattern,
            error: !isValidPattern,
            existing: false
        });
    };

    handleUpdate = () => this.props.onUpdate(this.state.text.trim());

    handleDelete = () => this.props.onDelete(this.state.text);

    render() {

        const { classes, isReadOnly, label, canBeDeleted, margin, datatype, pattern, allowEmpty,
            displayDatatype, invalidDatatype, dataTest, compact, placeHolder } = this.props;
        const { text, originalText, error, existing, isValidPattern } = this.state;

        const maxLength = datatype && DATATYPE_LIMITS[datatype] && DATATYPE_LIMITS[datatype].maxLength ?
            this.props.maxLength && this.props.maxLength < DATATYPE_LIMITS[datatype].maxLength ?
                this.props.maxLength
                :
                DATATYPE_LIMITS[datatype].maxLength
            :
            this.props.maxLength;

        let icon = null;
        const isEdited = text.toString().trim() !== originalText.toString().trim();
        if (error || (existing && text.toString() !== originalText.toString())) {
            icon =
                <InputAdornment position="end">
                    <IconButton title="The value is invalid" className={classes.smallIconButton}>
                        <ErrorIcon data-test="error" fontSize="small" />
                    </IconButton>
                </InputAdornment>;
        }
        else if (!isReadOnly && text.toString().trim().length > 0 && isEdited) {
            icon =
                <InputAdornment position="end">
                    <Tooltip title="Confirm change" onClick={this.handleUpdate}>
                        <IconButton className={classes.smallIconButton}>
                            <DoneIcon data-test="done" fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </InputAdornment>;
        }
        else if (!isReadOnly && canBeDeleted && !isEdited) {
            icon = <DeleteAdornment dataTest={dataTest} fontSize="small" onClick={this.handleDelete} />;
        }

        return (
            <ClickAwayListener onClickAway={text !== this.state.originalText || error ? this.reset : () => { }}>
                <div data-test={dataTest + "-container"}>
                    <TextField
                        value={text}
                        variant="outlined"
                        style={(datatype && displayDatatype) || compact ? { marginTop: '0px' } : {}}
                        margin={margin}
                        onChange={this.handleChange}
                        error={(!allowEmpty && text.toString().trim().length === 0) || text.toString().trim().length > maxLength || error || (existing && text.toString() !== originalText.toString())}
                        label={label}
                        fullWidth
                        placeholder={allowEmpty ? placeHolder : "You must enter text"}
                        onKeyPress={(ev) => {
                            if (!isReadOnly && text.toString().trim().length > 0 && ev.key === 'Enter') {
                                this.handleUpdate();
                                ev.preventDefault();
                                ev.stopPropagation();
                            }
                        }}
                        helperText={createLengthWarning(text, maxLength, isEdited)
                            || (invalidDatatype ? <span data-test="helper-text">This value is no longer valid for the property's datatype</span> : "")}
                        InputProps={{
                            className: classes.inputLabel,
                            startAdornment: datatype && displayDatatype ? <Datatype dataTest={dataTest + "-datatype"} datatype={datatype} invalid={invalidDatatype} /> : null,
                            endAdornment: icon,
                            inputProps: {
                                "data-test": dataTest + "-input",
                                readOnly: isReadOnly || invalidDatatype,
                                maxLength: maxLength
                            }
                        }
                        } />
                    {existing && text.toString() !== originalText.toString() && <FormError>This value already exists.</FormError>}
                    {(error || text.toString().trim().length > maxLength) && !existing && datatype !== "" && <FormError>{
                        text.toString().trim().length > maxLength ?
                            'Maximum is ' + maxLength + ' characters.'
                            :
                            isValidPattern ?
                                'The value is not valid for the datatype.'
                                :
                                'The text must match: ' + pattern
                    }</FormError>}
                </div>
            </ClickAwayListener>
        );
    }
}

LiteralConfirmationField.defaultProps = {
    isReadOnly: false,
    canBeDeleted: false,
    margin: "normal",
    existingValues: [],
    displayDatatype: true,
    invalidDatatype: false,
    compact: false,
    placeHolder: "",
    allowEmpty: false,
    allowSpaces: true,
    maxLength: 1000
};

LiteralConfirmationField.propTypes = {
    dataTest: PropTypes.string,
    isReadOnly: PropTypes.bool,
    compact: PropTypes.bool,
    margin: PropTypes.oneOf(['normal', 'dense']),
    onUpdate: PropTypes.func,
    onDelete: PropTypes.func,
    allowSpaces: PropTypes.bool,
    allowEmpty: PropTypes.bool,
    placeHolder: PropTypes.string,
    text: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    invalidDatatype: PropTypes.bool,
    existingValues: PropTypes.array,
    displayDatatype: PropTypes.bool,
    canBeDeleted: PropTypes.bool,
    datatype: PropTypes.string,
    maxLength: PropTypes.number,
    pattern: PropTypes.string,
    label: PropTypes.string,
    classes: PropTypes.object
};

export default withStyles(styles)(LiteralConfirmationField);